import {
  Alert,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Fab,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Fade,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useIncomesInRange } from "../../hooks/income";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Add as AddIcon,
  InsightsOutlined,
  LocalFireDepartmentOutlined,
  LoginOutlined, 
  PieChartOutline,
  SavingsOutlined,
  TrendingUp,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { getMonthsLongNames } from "../../utils/DateUtil";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import IncomeTableComponent from "../../components/finance/IncomeTableComponent";
import { PageContainer } from "../../components/common/StyledComponents";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import PrimaryButton from "../../components/common/ui/PrimaryButton";
import MonthYearPicker from "../../components/common/MonthYearPicker";
import { getMonthIndex, getFirstDayOfMonth, getLastDayOfMonth } from "../../utils/DateUtil";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";

const IncomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isAuthenticated } = useAuth();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const monthNames = getMonthsLongNames();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = Number(searchParams.get("year")) || currentYear;
  const urlMonth = searchParams.get("month") || monthNames[currentMonthIndex];

  const [selectedYear, setSelectedYear] = useState(urlYear);
  const [selectedMonth, setSelectedMonth] = useState(urlMonth);
  const [dataKey, setDataKey] = useState(0); // For triggering transitions
  const [quickAddTrigger, setQuickAddTrigger] = useState(0);
  const [showQuickAddFab, setShowQuickAddFab] = useState(true);
  const accent = theme.palette.success.main;
  const accentSoft = `${theme.palette.success.main}24`;
  const accentSofter = `${theme.palette.success.main}14`;

  const updateSearchParams = useCallback((newParams: Record<string, string>, replace = false) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams, { replace });
  }, [searchParams, setSearchParams]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    updateSearchParams({ year: year.toString() });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateSearchParams({ month });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const startDate = getFirstDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const endDate = getLastDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  
  const { data: incomes = [] } = useIncomesInRange(startDate, endDate);

  const handleIncomeChanged = () => {
    setDataKey(prev => prev + 1);
  };

  const handleQuickAddIncome = () => {
    setQuickAddTrigger((prev) => prev + 1);
  };

  const totalIncome = incomes.reduce((sum, income) => sum + income.amount, 0);
  const averageIncome = incomes.length > 0 ? totalIncome / incomes.length : 0;
  const highestIncome = incomes.length
    ? incomes.reduce((max, income) => (income.amount > max.amount ? income : max))
    : null;

  const inflowByCategory = incomes.reduce<Record<string, number>>((acc, income) => {
    acc[income.category] = (acc[income.category] || 0) + income.amount;
    return acc;
  }, {});

  const topSource = Object.entries(inflowByCategory).sort((a, b) => b[1] - a[1])[0];
  const topSources = Object.entries(inflowByCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const selectedMonthIndex = getMonthIndex(selectedMonth);
  const isCurrentPeriod = selectedYear === currentYear && selectedMonthIndex === currentMonthIndex;
  const daysInSelectedMonth = new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
  const elapsedDays = isCurrentPeriod ? today.getDate() : daysInSelectedMonth;
  const avgDailyIncome = elapsedDays > 0 ? totalIncome / elapsedDays : 0;
  const projectedMonthIncome = isCurrentPeriod ? avgDailyIncome * daysInSelectedMonth : totalIncome;
  const runRateDelta = projectedMonthIncome - totalIncome;

  const paceMessage =
    incomes.length === 0
      ? "No income recorded for this period yet. Add entries to unlock inflow pacing insights."
      : isCurrentPeriod
        ? `Current pace: ${formatCurrency(avgDailyIncome)}/day. Projected month inflow: ${formatCurrency(projectedMonthIncome)}.`
        : `Period complete: ${formatCurrency(totalIncome)} across ${incomes.length} income entries.`;

  const standoutIncomes = incomes
    .filter((income) => income.amount >= Math.max(averageIncome * 1.75, 1))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 3);

  const insights = [
    {
      label: "Total income",
      value: formatCurrency(totalIncome),
      hint: `${incomes.length} entries`,
      icon: <TrendingUp sx={{ color: accent }} />,
    },
    {
      label: "Average income",
      value: formatCurrency(averageIncome),
      hint: "Per transaction",
      icon: <SavingsOutlined sx={{ color: accent }} />,
    },
    {
      label: "Top source",
      value: topSource?.[0] ?? "No category yet",
      hint: topSource ? formatCurrency(topSource[1]) : "Add income to unlock",
      icon: <PieChartOutline sx={{ color: accent }} />,
    },
    {
      label: "Largest income",
      value: highestIncome ? formatCurrency(highestIncome.amount) : "None",
      hint: highestIncome ? highestIncome.category : "No data this month",
      icon: <LocalFireDepartmentOutlined sx={{ color: accent }} />,
    },
  ];

  useEffect(() => {
    if (isAuthenticated) {
      const needsInit = !searchParams.get("year") || !searchParams.get("month");
      if (needsInit) {
        updateSearchParams({
          year: selectedYear.toString(),
          month: selectedMonth,
        }, true);
      }
    }
  }, [isAuthenticated, searchParams, selectedMonth, selectedYear, updateSearchParams]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const updateFabVisibility = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const remaining = documentHeight - (scrollTop + viewportHeight);
      const shouldShow = documentHeight <= viewportHeight + 40 || remaining > 220;
      setShowQuickAddFab(shouldShow);
    };

    updateFabVisibility();
    window.addEventListener("scroll", updateFabVisibility, { passive: true });
    window.addEventListener("resize", updateFabVisibility);

    return () => {
      window.removeEventListener("scroll", updateFabVisibility);
      window.removeEventListener("resize", updateFabVisibility);
    };
  }, [isAuthenticated, selectedMonth, selectedYear, dataKey]);

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
          <SurfaceCard
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              borderRadius: "18px",
              border: `1px solid ${accentSoft}`,
              background: `linear-gradient(165deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: "auto -30% -65% auto",
                width: 420,
                height: 420,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${accentSoft} 0%, transparent 60%)`,
              },
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                backgroundColor: accentSoft,
                color: theme.palette.success.light,
              }}
            >
              <TrendingUp />
            </Avatar>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              Income Growth Hub
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: "auto", mb: 3 }}>
              Track every incoming payment source with a modern monthly command center built for clear forecasting and stronger financial momentum.
            </Typography>
            <PrimaryButton startIcon={<LoginOutlined />} onClick={() => navigate(LOGIN_ROUTE_PATH)} sx={{ px: 4, py: 1.5 }}>
              Sign In to Open Income
            </PrimaryButton>
          </SurfaceCard>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 9, md: 4 } }}>
        <Fade in={true} timeout={400}>
          <Box>
            <SurfaceCard
              sx={{
                mb: 3,
                p: { xs: 2.5, md: 4 },
                borderRadius: "18px",
                border: `1px solid ${accentSoft}`,
                background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: "-180px auto auto -120px",
                  width: 340,
                  height: 340,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${accentSoft} 0%, transparent 66%)`,
                },
              }}
            >
              <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: accentSoft, color: theme.palette.success.light }}>
                      <InsightsOutlined />
                    </Avatar>
                    <Chip
                      label={`${selectedMonth} ${selectedYear}`}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 700,
                        backgroundColor: accentSofter,
                        color: theme.palette.success.light,
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      mb: 1,
                    }}
                  >
                    Monthly Income Overview
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700 }}>
                    Monitor inflows with clarity, identify your highest-value sources, and forecast your month with confidence.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Alert
                    severity="success"
                    icon={false}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: accentSofter,
                      border: `1px solid ${accentSoft}`,
                      color: theme.palette.success.light,
                      py: 0.8,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {paceMessage}
                  </Alert>
                </Grid>
              </Grid>
            </SurfaceCard>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {insights.map((item, index) => (
                <Grid item key={item.label} xs={12} sm={6} lg={3}>
                  <Fade in={true} timeout={350 + index * 120}>
                    <SurfaceCard
                      sx={{
                        p: 2,
                        borderRadius: "16px",
                        border: `1px solid ${accentSoft}`,
                        background: `linear-gradient(170deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                        transition: "transform 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          borderColor: accent,
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.label}
                        </Typography>
                        {item.icon}
                      </Stack>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.hint}
                      </Typography>
                    </SurfaceCard>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} lg={8}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px", overflow: "hidden" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Analyze Another Period
                  </Typography>
                  <MonthYearPicker
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    onMonthChange={handleMonthChange}
                    onYearChange={handleYearChange}
                    colorTheme="success"
                    transactionCount={incomes.length}
                    isLoading={false}
                  />
                </SurfaceCard>
              </Grid>
              <Grid item xs={12} lg={4}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, height: "100%", borderRadius: "18px" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Focus Recommendation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Strongest source this month:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      color: theme.palette.success.light,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {topSource?.[0] ?? "No data"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    {topSource ? `${formatCurrency(topSource[1])} across current period` : "Track at least one income to generate a recommendation."}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Largest income entry:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {highestIncome ? `${formatCurrency(highestIncome.amount)} - ${highestIncome.category}` : "No income yet"}
                  </Typography>
                </SurfaceCard>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px", height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
                    Monthly Run Rate
                  </Typography>
                  <Stack spacing={1.2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Average daily income
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {formatCurrency(avgDailyIncome)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {isCurrentPeriod ? "Projected month income" : "Final month income"}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.light }}>
                        {formatCurrency(projectedMonthIncome)}
                      </Typography>
                    </Box>

                    <Chip
                      label={
                        isCurrentPeriod
                          ? `${runRateDelta >= 0 ? "+" : ""}${formatCurrency(runRateDelta)} vs current total`
                          : "Month complete"
                      }
                      sx={{
                        width: "fit-content",
                        borderRadius: "10px",
                        backgroundColor: accentSofter,
                        color: theme.palette.success.light,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                </SurfaceCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px", height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
                    Source Allocation
                  </Typography>

                  {topSources.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Add income entries to see where most of your inflows originate.
                    </Typography>
                  ) : (
                    <Stack spacing={1.1}>
                      {topSources.map(([category, amount]) => {
                        const ratio = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;

                        return (
                          <Box key={category}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.4 }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {category}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(amount)} ({ratio.toFixed(0)}%)
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, Math.max(0, ratio))}
                              sx={{
                                height: 7,
                                borderRadius: 10,
                                backgroundColor: `${theme.palette.text.primary}14`,
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 10,
                                  background: `linear-gradient(90deg, ${theme.palette.success.dark}, ${theme.palette.success.light})`,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </SurfaceCard>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: accentSofter, color: theme.palette.success.light }}>
                      <WarningAmberOutlined sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        High-Value Inflows
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Entries significantly above your average income amount
                      </Typography>
                    </Box>
                  </Stack>

                  {standoutIncomes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No standout inflows in this period yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {standoutIncomes.map((income) => (
                        <Box
                          key={income.id}
                          sx={{
                            p: 1.2,
                            borderRadius: "10px",
                            border: `1px solid ${accentSoft}`,
                            backgroundColor: `${theme.palette.background.default}80`,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {income.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {income.category} • {new Date(income.date).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Chip
                              label={formatCurrency(income.amount)}
                              sx={{
                                borderRadius: "8px",
                                backgroundColor: `${theme.palette.success.main}1f`,
                                color: theme.palette.success.light,
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </SurfaceCard>
              </Grid>
            </Grid>

            <Fade in={true} timeout={600} key={dataKey}>
              <SurfaceCard sx={{ overflow: "hidden", borderRadius: "18px" }}>
                <IncomeTableComponent
                  year={selectedYear}
                  month={selectedMonth}
                  onIncomeChange={handleIncomeChanged}
                  addEntryTrigger={quickAddTrigger}
                />
              </SurfaceCard>
            </Fade>

            <Fab
              color="success"
              variant={isMobile ? "circular" : "extended"}
              onClick={handleQuickAddIncome}
              aria-label="Add income"
              sx={{
                position: "fixed",
                right: { xs: 16, md: 28 },
                bottom: { xs: "calc(82px + env(safe-area-inset-bottom))", md: 28 },
                zIndex: (theme) => theme.zIndex.appBar + 2,
                px: 2,
                borderRadius: 999,
                boxShadow: "0 14px 34px rgba(15,23,42,0.45)",
                opacity: showQuickAddFab ? 1 : 0,
                transform: showQuickAddFab ? "translateY(0)" : "translateY(16px)",
                pointerEvents: showQuickAddFab ? "auto" : "none",
                transition: "opacity 180ms ease, transform 180ms ease",
              }}
            >
              <AddIcon sx={{ mr: { xs: 0, md: 0.8 } }} />
              {!isMobile && "Add Income"}
            </Fab>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default IncomePage;
